import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import {
  STAGES, STAGE_PRODUCTION, FINAL_PHOTO_STAGE, FINAL_PHOTO_CATEGORY, CAN_STAGE_PROD, CAN_STAGE_BACK, DEAL_VIEW,
} from "@/lib/constants";
import { fmtDate, fmtDateTime, fmtRupiah } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Clock, Camera, ImagePlus, ArrowRight, Printer, Lock } from "lucide-react";

export default function RepairOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ro, setRo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [deal, setDeal] = useState(null);
  const canViewDeal = user && DEAL_VIEW.includes(user.role);
  useEffect(() => { if (canViewDeal) { api.get(`/projects/${id}/deal`).then(({ data }) => setDeal(data)).catch(() => {}); } }, [id, canViewDeal]);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/repair-orders/${id}`); setRo(data); }
    catch { toast.error("Repair Order tidak ditemukan"); navigate("/repair-orders"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (loading || !ro) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const flow = ro.stage_flow || STAGES;
  const curIdx = flow.indexOf(ro.stage);
  const nextStage = flow[curIdx + 1];
  const inProd = STAGE_PRODUCTION.includes(ro.stage);
  const canAdvance = user && (nextStage && (STAGE_PRODUCTION.includes(nextStage) ? CAN_STAGE_PROD.includes(user.role) : ["Owner", "Manager", "Kepala Produksi"].includes(user.role)));
  const canBack = user && CAN_STAGE_BACK.includes(user.role);
  const hasFinal = (ro.photos || []).some((ph) => (ph.category || "").toLowerCase().includes("hasil akhir"));
  const imgUrl = (p) => `${API}/files/${p}`;
  const lastNote = (() => {
    const h = (ro.stage_history || []).filter((x) => x.note);
    if (h.length) return h[h.length - 1].note;
    const dr = ro.daily_reports || [];
    return dr.length ? dr[0].description : null;
  })();
  const initialPhotos = (ro.photos || []).filter((ph) => !(ph.category || "").toLowerCase().includes("hasil akhir") && !(ph.category || "").toLowerCase().includes("progres"));

  const uploadPhoto = async (file, category) => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); fd.append("category", category); await api.post(`/projects/${id}/photos`, fd); toast.success("Foto diunggah"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const advance = async () => {
    if (!nextStage) return;
    if (nextStage === FINAL_PHOTO_STAGE && !hasFinal) { toast.error(`Unggah foto hasil akhir dulu (tombol di bawah).`); return; }
    try { await api.patch(`/projects/${id}/stage`, { stage: nextStage, note }); toast.success(`Tahap: ${nextStage}`); setNote(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const goBack = async (stg) => {
    const reason = window.prompt(`Alasan memundurkan tahap ke "${stg}" (wajib):`) || "";
    if (!reason) return;
    try { await api.patch(`/projects/${id}/stage`, { stage: stg, reason }); toast.success(`Dimundurkan ke ${stg}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4 pb-16" data-testid="ro-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="back-button"><ArrowLeft size={18} /></Button>
        <div>
          <div className="font-mono-k3 text-sm font-bold text-emerald-700">{ro.ro_number}</div>
          <h1 className="font-heading text-lg font-bold text-navy">{ro.plate} · {ro.customer_name}</h1>
          <span className="font-mono-k3 text-[11px] text-primary">{ro.project_id}</span>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${id}/spk?v=internal`)} data-testid="ro-spk-internal-btn"><Printer size={14} className="mr-1.5" />SPK Produksi</Button>
          {canViewDeal && <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${id}/spk?v=customer`)} data-testid="ro-spk-customer-btn"><Printer size={14} className="mr-1.5" />SPK Customer</Button>}
        </div>
      </div>

      {canViewDeal && deal && (
        <Card className="p-3 flex items-center justify-between flex-wrap gap-2" data-testid="ro-deal-summary">
          <span className="text-sm text-muted-foreground flex items-center gap-1.5">{deal.locked && <Lock size={13} />} Total Nilai Kontrak (Harga Deal + addendum disetujui)</span>
          <span className="font-bold text-primary" data-testid="ro-deal-contract-total">{deal.exists ? fmtRupiah(deal.contract_total) : "Belum ditetapkan"}</span>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4 space-y-1">
          <h3 className="font-heading font-semibold text-navy text-sm mb-1">Informasi Unit</h3>
          <div className="text-sm flex justify-between"><span className="text-muted-foreground">Tahap Saat Ini</span><StatusBadge status={ro.stage} /></div>
          <div className="text-sm flex justify-between"><span className="text-muted-foreground">Jenis Kendaraan</span><span>{ro.vehicle?.vehicle_type || "-"}</span></div>
          <div className="text-sm flex justify-between"><span className="text-muted-foreground">Merek/Tipe</span><span>{ro.brand} {ro.model}</span></div>
          <div className="text-sm flex justify-between"><span className="text-muted-foreground">Pekerjaan</span><span>{ro.work_type}</span></div>
          <div className="text-sm flex justify-between"><span className="text-muted-foreground">Penanggung Jawab</span><span>{ro.penanggung_jawab_name || <span className="text-primary">Belum ada PJ</span>}</span></div>
          <div className="text-sm flex justify-between"><span className="text-muted-foreground">Tanggal Masuk</span><span>{ro.unit_in_at ? fmtDateTime(ro.unit_in_at) : "-"}</span></div>
          <div className="text-sm flex justify-between"><span className="text-muted-foreground">Target Selesai</span><span>{fmtDate(ro.target_date)}</span></div>
          <div className="text-sm flex justify-between"><span className="text-muted-foreground">Hari Berjalan</span><span>{ro.days_running != null ? `${ro.days_running} hari` : "-"}</span></div>
          <div className="text-sm"><span className="text-muted-foreground">Catatan Terakhir:</span> <span className="font-medium">{lastNote || "-"}</span></div>
        </Card>

        <Card className="p-4 space-y-2">
          <h3 className="font-heading font-semibold text-navy text-sm">Update Status Produksi</h3>
          {!canAdvance && !canBack ? <p className="text-xs text-muted-foreground">Anda tidak memiliki hak mengubah tahap.</p> : (
            <>
              <Textarea placeholder="Catatan progres (wajib diisi saat update)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="ro-note" />
              {inProd && (
                <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm cursor-pointer hover:bg-slate-50 w-full" data-testid="ro-progress-photo-label">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Tambah Foto Progres (opsional)
                  <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="ro-progress-photo" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0], "Foto Progres")} />
                </label>
              )}
              {nextStage === FINAL_PHOTO_STAGE && (
                <label className={`inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm cursor-pointer w-full ${hasFinal ? "border-emerald-300 bg-emerald-50" : "border-primary bg-red-50"}`} data-testid="ro-final-photo-label">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} {hasFinal ? "Foto hasil akhir sudah ada" : "Unggah Foto Hasil Akhir (wajib untuk Ready)"}
                  <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="ro-final-photo" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0], FINAL_PHOTO_CATEGORY)} />
                </label>
              )}
              <div className="flex flex-wrap gap-2">
                {nextStage && canAdvance && (
                  <Button className="bg-primary hover:bg-red-700 h-11 text-base flex-1 sm:flex-none" data-testid="ro-advance-btn" onClick={advance}>Lanjut ke {nextStage} <ArrowRight size={16} className="ml-1.5" /></Button>
                )}
                {!nextStage && <span className="text-xs text-emerald-700 font-medium self-center">Tahap akhir tercapai.</span>}
                {canBack && curIdx > 0 && (
                  <Select onValueChange={goBack}>
                    <SelectTrigger className="w-[170px] h-11" data-testid="ro-back-select"><SelectValue placeholder="Mundur (alasan)" /></SelectTrigger>
                    <SelectContent>{flow.slice(0, curIdx).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
            </>
          )}
        </Card>
      </div>

      {initialPhotos.length > 0 && (
        <Card className="p-4">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3 flex items-center gap-2"><Camera size={16} /> Foto Awal</h3>
          <div className="flex gap-2 overflow-x-auto">
            {initialPhotos.map((ph) => <img key={ph.id} src={imgUrl(ph.storage_path)} alt={ph.category} title={ph.category} className="h-24 w-24 object-cover rounded border shrink-0" />)}
          </div>
        </Card>
      )}

      <Card className="p-4">
        <h3 className="font-heading font-semibold text-navy text-sm mb-3 flex items-center gap-2"><Clock size={16} /> Riwayat Perubahan Status</h3>
        <div className="space-y-3">
          {(ro.stage_durations || []).map((h, i) => (
            <div key={i} className="flex gap-3 text-sm" data-testid={`ro-timeline-${i}`}>
              <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">{h.status} <span className="text-xs text-muted-foreground">· {h.duration_minutes} menit</span></div>
                <div className="text-xs text-muted-foreground">{fmtDateTime(h.at)} · {h.by}{h.note ? ` · ${h.note}` : ""}</div>
              </div>
            </div>
          ))}
          {(!ro.stage_durations || ro.stage_durations.length === 0) && <p className="text-sm text-muted-foreground">Belum ada riwayat produksi.</p>}
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="font-heading font-semibold text-navy text-sm mb-3 flex items-center gap-2"><Camera size={16} /> Daily Report ({ro.daily_reports?.length || 0})</h3>
        {(!ro.daily_reports || ro.daily_reports.length === 0) ? <p className="text-sm text-muted-foreground">Belum ada laporan.</p> : (
          <div className="space-y-2">
            {ro.daily_reports.map((r) => (
              <div key={r.id} className="border rounded-md p-2.5" data-testid={`ro-report-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{r.stage} · {r.progress}%</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100">{r.status}</span>
                </div>
                <div className="text-xs text-muted-foreground">{r.reporter_name} · {fmtDateTime(r.created_at)}</div>
                <div className="text-xs mt-1">{r.description}</div>
                {r.photos?.length > 0 && (
                  <div className="flex gap-1.5 mt-2 overflow-x-auto">
                    {r.photos.map((ph) => <img key={ph.id} src={imgUrl(ph.storage_path)} alt={ph.caption} className="h-16 w-16 object-cover rounded border shrink-0" />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
